import {
  useCreatePortalSession,
  useDowngradeAtPeriodEnd,
  useGetSubscriptionDetails,
} from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { usePricingData } from '@/hooks/usePricingData';
import { getFreePlanProductId } from '@/services/subscriptionService';
import { Box, CardContent, CircularProgress, Typography, Divider, useTheme, useMediaQuery } from '@mui/material';
import { CalendarToday, CreditCard } from '@mui/icons-material';
import {
  LeftSide,
  SettingBox,
  StyledBox,
  StyledButton,
  StyledCard,
  StyledLoadingContainer,
  StyledSection,
  Subtitle,
} from '../styles';
import { formatDateForNoteWithoutTime } from '@/helpers/formatDate';
import { useState } from 'react';
import CancelSubscriptionModal from '@/components/CustomModal/CancelSubscriptionModal';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';

const ActiveSubscriptionDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userToken, userData, fetchAndSetUserData } = useAuthStore();
  const { pricingData: planData, loading: planLoading } = usePricingData();
  const {
    data: subscriptionData,
    error: subscriptionDataError,
    isLoading: isLoadingSubscription,
    refetch: refetchSubscription,
  } = useGetSubscriptionDetails({
    headers: { authorization: `Bearer ${userToken}` },
  });
  const [loadingPortalSession, setLoadingPortalSession] = useState(false);
  const [loadingCancelSubscription, setLoadingCancelSubscription] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showAlert = useAlertStore((state) => state.showAlert);

  const { mutate: getPortalSession } = useCreatePortalSession();

  const { mutate: downgradeSubscription } = useDowngradeAtPeriodEnd();

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDowngradeToFree = async () => {
    try {
      setLoadingCancelSubscription(true);
      const freeProductId = await getFreePlanProductId();
      if (!freeProductId) {
        showAlert('Unable to determine free plan. Please refresh the page and try again.', 'error');
        setLoadingCancelSubscription(false);
        return;
      }
      downgradeSubscription(
        {
          body: { product_id: freeProductId },
          headers: { authorization: `Bearer ${userToken}` },
        },
        {
          onSuccess: () => {
            showAlert('Your subscription has been downgraded to the Free plan.', 'success');
          },
          onError: (err) => {
            errorReporter.critical('Failed to downgrade subscription to free', err, {
              feature: 'stripe',
              operation: 'downgradeAtPeriodEnd',
            });
            showAlert('Failed to downgrade subscription. Please try again.', 'error');
          },
          onSettled: () => {
            refetchSubscription();
            fetchAndSetUserData?.({ force: true });
            setLoadingCancelSubscription(false);
            setIsModalOpen(false);
          },
        },
      );
    } catch (error) {
      errorReporter.critical('Failed to downgrade subscription to free', error, {
        feature: 'stripe',
        operation: 'downgradeAtPeriodEnd',
      });
      showAlert('Failed to downgrade subscription. Please try again.', 'error');
      setLoadingCancelSubscription(false);
    }
  };

  const handleManageSubscription = () => {
    setLoadingPortalSession(true);
    getPortalSession(
      {
        headers: { authorization: `Bearer ${userToken}` },
      },
      {
        onSuccess: (data) => {
          window.open(data.portal_url, '_blank');
        },
        onError: (err) => {
          errorReporter.critical('Failed to create portal session', err, {
            feature: 'stripe',
            operation: 'createPortalSession',
          });
          showAlert('Failed to open subscription management. Please try again.', 'error');
        },
        onSettled: () => {
          setLoadingPortalSession(false);
        },
      },
    );
  };

  if (isLoadingSubscription) {
    return (
      <SettingBox>
        <LeftSide>
          <Typography variant="body1" fontWeight="700">
            Plans and Payments
          </Typography>
          <Subtitle variant="body2">You can change your personal information settings here.</Subtitle>
        </LeftSide>
        <StyledCard>
          <StyledLoadingContainer>
            <CircularProgress />
          </StyledLoadingContainer>
        </StyledCard>
      </SettingBox>
    );
  }

  if (subscriptionDataError || !subscriptionData) {
    return null;
  }

  // Determine current plan name based on the user's price_id and fetched pricing data
  const currentPriceId = userData?.subscription?.price_id;
  const currentProductId = userData?.subscription?.product_id;
  let planName: string | null = null;

  if (planData && userData?.subscription) {
    // First try to find by price_id (most accurate for paid plans)
    if (currentPriceId) {
      const pricePlan = Object.values(planData).find(
        (tier) =>
          tier.prices?.month?.stripePriceId === currentPriceId || tier.prices?.year?.stripePriceId === currentPriceId,
      );

      if (pricePlan) {
        planName = pricePlan.name;
      }
    }

    // If not found by price_id, try by product_id (for free plan)
    if (!planName && currentProductId) {
      const productPlan = Object.values(planData).find((tier) => tier.product_id === currentProductId);

      if (productPlan) {
        planName = productPlan.name;
      }
    }

    // If not found by price_id or product_id, check for lifetime cap plan
    // Lifetime plans don't have Stripe fields, so we use product_name or plan field
    if (!planName && userData.subscription.plan_type === 'lifetime_cap') {
      planName = userData.subscription.product_name || userData.subscription.plan || 'Lifetime Plan';
    }

    if (!planName) {
      planName = 'Unknown Plan';
    }
  }

  // Determine if this is a lifetime cap plan (no Stripe billing)
  const isLifetimePlan = userData?.subscription?.plan_type === 'lifetime_cap';

  return (
    <>
      <SettingBox>
        <LeftSide>
          <Typography variant="body1" fontWeight="700">
            Plans and Payments
          </Typography>
          <Subtitle variant="body2">You can change your personal information settings here.</Subtitle>
        </LeftSide>
        <StyledCard>
          <CardContent>
            <StyledSection>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                Current plan
              </Typography>
              <Typography>{planLoading ? 'Loading plan…' : planName || 'Unknown plan'}</Typography>
              {subscriptionData.cancel_at_period_end && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontWeight: 600 }}>
                  ⚠️ Subscription will end on {formatDateForNoteWithoutTime(subscriptionData.next_billing_date)}
                </Typography>
              )}
            </StyledSection>
            {!isLifetimePlan && subscriptionData.billing_cycle && (
              <StyledSection>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                  Billing type
                </Typography>
                <Typography>
                  {subscriptionData.billing_cycle === 'year' ? 'Billed annually' : 'Billed monthly'}
                </Typography>
              </StyledSection>
            )}

            <Box display="flex" gap={isMobile ? 2 : 4} flexDirection={isMobile ? 'column' : 'row'}>
              <StyledSection flex={1}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                  {isLifetimePlan
                    ? 'Billing'
                    : subscriptionData.canceled_at !== null || subscriptionData.cancel_at_period_end
                      ? 'Subscription end date'
                      : 'Next billing date'}
                </Typography>
                <StyledBox>
                  <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {isLifetimePlan
                      ? 'No billing (Lifetime plan)'
                      : formatDateForNoteWithoutTime(subscriptionData.next_billing_date)}
                  </Typography>
                  <CalendarToday color="action" />
                </StyledBox>
              </StyledSection>

              <StyledSection flex={1}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                  Payment card
                </Typography>
                <StyledBox>
                  <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {isLifetimePlan
                      ? 'N/A'
                      : typeof subscriptionData.last_four !== 'string' || subscriptionData.last_four === 'N/A'
                        ? 'Card details hidden'
                        : `Card ending in ${subscriptionData.last_four}`}
                  </Typography>
                  <CreditCard color="action" />
                </StyledBox>
              </StyledSection>
            </Box>

            <Divider />

            {!isLifetimePlan && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'space-between',
                  mt: 3,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <StyledButton
                  variant="outlined"
                  color="error"
                  onClick={handleOpenModal}
                  disabled={
                    !!loadingCancelSubscription ||
                    subscriptionData.canceled_at !== null ||
                    !!subscriptionData.cancel_at_period_end
                  }
                  fullWidth={isMobile}
                >
                  {loadingCancelSubscription ? <CircularProgress size={24} /> : 'Downgrade to Free'}
                </StyledButton>

                <StyledButton
                  variant="contained"
                  onClick={handleManageSubscription}
                  disabled={loadingPortalSession}
                  fullWidth={isMobile}
                >
                  {loadingPortalSession ? <CircularProgress size={24} /> : 'Subscription management'}
                </StyledButton>
              </Box>
            )}
            {isLifetimePlan && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  This is an admin-assigned lifetime plan. Contact support for any plan changes.
                </Typography>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </SettingBox>

      <CancelSubscriptionModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        text="Are you sure you want to downgrade to the Free plan? This takes effect immediately and your current plan's features will end now."
        confirmButtonText="Downgrade to Free"
        onConfirm={handleDowngradeToFree}
        isLoading={loadingCancelSubscription}
      />
    </>
  );
};

export default ActiveSubscriptionDetails;
